export function filterAlcoholicItemFromPurchase(
  purchase: Purchase,
): Purchase | undefined {
  if (purchase.item.alcoholic) {
    return;
  }

  const filteredItemChoiceSets = [];
  const filteredChoiceSets = [];

  for (const choiceSet of purchase.item.choiceSets) {
    filteredItemChoiceSets.push(filterAlcoholicItemsFromChoiceSet(choiceSet));
  }

  for (const choiceSet of purchase.choiceSets) {
    filteredChoiceSets.push(filterAlcoholicItemsFromChoiceSet(choiceSet));
  }

  const filteredSizes = purchase.item.sizes.filter(size => !size.alcoholic);

  return {
    ...purchase,
    item: {
      ...purchase.item,
      choiceSets: filteredItemChoiceSets,
      sizes: filteredSizes,
    },
    choiceSets: filteredChoiceSets as (
      | ValidatedChoiceSet
      | ValidatedNestedChoiceSet
    )[],
  };
}

export function filterAlcoholicItemsFromChoiceSet(
  choiceSet:
    | ChoiceSetWithQuantity
    | RedCat.UpsellChoiceSet
    | ChoiceSet
    | NestedChoiceSet,
):
  | ChoiceSetWithQuantity
  | RedCat.UpsellChoiceSet
  | ChoiceSet
  | NestedChoiceSet {
  if (choiceSet == undefined) {
    return choiceSet;
  }

  const filteredChoices = choiceSet.choices
    .filter(choice => !choice.alcoholic)
    .map(choice => {
      if ('choiceSets' in choice && choice.choiceSets) {
        const nestedChoice = choice as NestedChoice;
        return {
          ...nestedChoice,
          choiceSets: nestedChoice.choiceSets.map(nestedChoiceSet =>
            filterAlcoholicItemsFromChoiceSet(nestedChoiceSet),
          ),
        };
      }
      return choice;
    });

  return {
    ...choiceSet,
    choices: filteredChoices,
  };
}

export function itemContainsAlcoholicItems(item: Item): boolean {
  if (item.alcoholic) {
    return true;
  }

  if (item.sizes && item.sizes.some(size => size.alcoholic)) {
    return true;
  }

  if (
    item.choiceSets &&
    item.choiceSets.some(choiceSet => {
      return choiceSetContainsAlcoholicItems(choiceSet);
    })
  ) {
    return true;
  }

  return false;
}

export function choiceSetContainsAlcoholicItems(
  choiceSet:
    | ChoiceSetWithQuantity
    | RedCat.UpsellChoiceSet
    | ChoiceSet
    | NestedChoiceSet,
): boolean {
  for (const choice of choiceSet.choices) {
    if (choice.alcoholic) {
      return true;
    }

    if ('choiceSets' in choice && choice.choiceSets) {
      const nestedChoice = choice as NestedChoice;
      for (const nestedChoiceSet of nestedChoice.choiceSets) {
        if (choiceSetContainsAlcoholicItems(nestedChoiceSet)) {
          return true;
        }
      }
    }
  }

  return false;
}

export function stagedPurchaseContainsAlcoholicItems(
  stagedPurchase: Purchase,
): boolean {
  if (stagedPurchase.item.alcoholic) {
    return true;
  }

  if (stagedPurchase.sizeId) {
    const selectedSize = stagedPurchase.item.sizes.find(
      size => size.id === stagedPurchase.sizeId,
    );
    if (selectedSize && itemContainsAlcoholicItems(selectedSize)) {
      return true;
    }
  }

  if (
    stagedPurchase.choicesWithQuantity &&
    stagedPurchase.choicesWithQuantity.length > 0
  ) {
    for (const choiceWithQuantity of stagedPurchase.choicesWithQuantity as unknown as Item[]) {
      if (choiceWithQuantity.alcoholic) {
        return true;
      }

      const item = choiceWithQuantity as unknown as Item;

      if (
        item.choiceSets &&
        item.choiceSets.some(choiceSet =>
          choiceSetContainsAlcoholicItems(choiceSet),
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

export function categoryContainsAlcoholicItems(category: Category): boolean {
  if (category.items.some(item => itemContainsAlcoholicItems(item))) {
    return true;
  }

  return false;
}

export function itemRequiresAlcohol(item: Item): boolean {
  if (item.alcoholic) {
    return true;
  }

  if (item.choiceSets) {
    const requiredChoiceSets = item.choiceSets.filter(
      choiceSet => choiceSet.required,
    );

    for (const choiceSet of requiredChoiceSets) {
      const filteredChoiceSet = filterAlcoholicItemsFromChoiceSet(choiceSet);

      if (filteredChoiceSet.choices.length === 0) {
        return true;
      }

      if (
        filteredChoiceSet.required &&
        filteredChoiceSet.individualMax &&
        filteredChoiceSet.min
      ) {
        const nonAlcoholicChoices = filteredChoiceSet.choices.length;

        if (
          nonAlcoholicChoices * filteredChoiceSet.individualMax <
          filteredChoiceSet.min
        ) {
          return true;
        }
      }

      const nestedChoices = (
        filteredChoiceSet.choices as NestedChoice[]
      ).filter(choice => choice.choiceSets);

      if (
        nestedChoices.some(choice =>
          itemRequiresAlcohol(choice as unknown as Item),
        )
      ) {
        return true;
      }
    }
  }

  if (item.sizes && item.sizes.length > 0) {
    if (item.sizes.every(size => size.alcoholic)) {
      return true;
    }

    if (item.sizes.every(size => itemRequiresAlcohol(size))) {
      return true;
    }
  }

  return false;
}
